import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion } from "framer-motion";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Plus, ArrowRight, Layers, Coins, Handshake, Users, Building2, Landmark } from "lucide-react";

const TYPE_META = {
  supplier: { icon: Handshake, c: "#8B5CF6", key: "form.supplier" },
  union: { icon: Users, c: "#F59E0B", key: "form.union" },
  rent: { icon: Building2, c: "#EAB308", key: "form.rent" },
  tax: { icon: Landmark, c: "#3B82F6", key: "form.tax" },
};

const fmtEur = (n) => new Intl.NumberFormat("fr-FR", { maximumFractionDigits: 0 }).format(n || 0) + " €";

export default function Dashboard() {
  const { t } = useTranslation();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [recent, setRecent] = useState([]);

  useEffect(() => {
    api.get("/simulations/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/simulations").then((r) => setRecent(r.data.slice(0, 5))).catch(() => {});
  }, []);

  return (
    <div className="min-h-screen bg-[#0B0E14]">
      <Navbar />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-10">
          <div>
            <p className="text-xs tracking-[0.2em] uppercase text-muted-foreground mb-2">{t("dash.welcome")}, {user?.name?.split(" ")[0]}</p>
            <h1 className="font-heading font-bold text-3xl sm:text-4xl tracking-tight">{t("dash.title")}</h1>
          </div>
          <Button data-testid="dash-new-sim-btn" onClick={() => navigate("/new")}
            className="bg-primary text-white hover:bg-[#6D28D9] rounded-lg shadow-[0_0_15px_rgba(124,58,237,0.3)]">
            <Plus className="h-4 w-4 mr-2" /> {t("dash.new_sim")}
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-10">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
            className="rounded-xl bg-[#151A22] border border-border p-6" data-testid="stat-total">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t("dash.total")}</span>
              <Layers className="h-5 w-5 text-primary" />
            </div>
            <div className="font-mono-fin font-semibold text-4xl mt-3">{stats?.total ?? "—"}</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.06 }}
            className="rounded-xl bg-[#151A22] border border-border p-6" data-testid="stat-contested">
            <div className="flex items-center justify-between">
              <span className="text-sm text-muted-foreground">{t("dash.total_contested")}</span>
              <Coins className="h-5 w-5 text-[#EAB308]" />
            </div>
            <div className="font-mono-fin font-semibold text-3xl mt-3 text-[#EAB308]">{stats ? fmtEur(stats.total_contested) : "—"}</div>
          </motion.div>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4, delay: 0.12 }}
            className="rounded-xl bg-[#151A22] border border-border p-6" data-testid="stat-bytype">
            <span className="text-sm text-muted-foreground">{t("dash.by_type")}</span>
            <div className="flex flex-wrap gap-2 mt-4">
              {stats && Object.keys(stats.by_type).length > 0 ? Object.entries(stats.by_type).map(([k, v]) => {
                const m = TYPE_META[k]; const Icon = m?.icon || Layers;
                return (
                  <span key={k} className="inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-mono-fin"
                    style={{ borderColor: `${m?.c}55`, background: `${m?.c}1a`, color: m?.c }}>
                    <Icon className="h-3.5 w-3.5" /> {t(m?.key || k)} · {v}
                  </span>
                );
              }) : <span className="text-muted-foreground text-sm">—</span>}
            </div>
          </motion.div>
        </div>

        {/* Recent */}
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-bold text-xl">{t("dash.recent")}</h2>
          <Link to="/history" className="text-sm text-primary hover:underline flex items-center gap-1" data-testid="dash-viewall-link">
            {t("dash.view_all")} <ArrowRight className="h-3.5 w-3.5" />
          </Link>
        </div>

        {recent.length === 0 ? (
          <div className="rounded-xl bg-[#151A22] border border-dashed border-border p-12 text-center" data-testid="dash-empty">
            <p className="text-muted-foreground mb-5">{t("dash.empty")}</p>
            <Button data-testid="dash-empty-cta" onClick={() => navigate("/new")} className="bg-primary text-white hover:bg-[#6D28D9] rounded-lg">
              <Plus className="h-4 w-4 mr-2" /> {t("dash.new_sim")}
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {recent.map((s) => {
              const m = TYPE_META[s.conflict_type]; const Icon = m?.icon || Layers;
              return (
                <button key={s.id} onClick={() => navigate(`/simulation/${s.id}`)} data-testid={`recent-sim-${s.id}`}
                  className="w-full text-left rounded-xl bg-[#151A22] border border-border p-5 flex items-center gap-4 hover:-translate-y-0.5 hover:border-[#3a4456] transition-all">
                  <div className="h-10 w-10 rounded-lg flex items-center justify-center shrink-0" style={{ background: `${m?.c}1f`, border: `1px solid ${m?.c}55` }}>
                    <Icon className="h-5 w-5" style={{ color: m?.c }} />
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="font-medium truncate">{s.title}</div>
                    <div className="text-xs text-muted-foreground font-mono-fin">{new Date(s.created_at).toLocaleDateString()}</div>
                  </div>
                  {s.contested_amount ? <div className="font-mono-fin text-sm text-[#EAB308] shrink-0">{fmtEur(s.contested_amount)}</div> : null}
                  <ArrowRight className="h-4 w-4 text-muted-foreground shrink-0" />
                </button>
              );
            })}
          </div>
        )}
      </main>
    </div>
  );
}
